import { isPlatformServer } from '@angular/common';
import { inject, NgZone, PLATFORM_ID } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DOMAIN, GET_ADD_LOCATION_MENU_API, SITE_REQUEST_FROM } from 'src/app/global_constant';
import { MainService } from 'src/app/main.service';

export interface ListingFilterSources {
	filterCountries: Observable<any[]>;
	filterCategories: Observable<any[]>;
	filterHighLevel: Observable<any[]>;
}

interface SlimPlace {
	id: any;
	name: string;
	slug: string;
}

const COUNTRY_CACHE_MS = 15 * 60 * 1000;
let cachedCountries: SlimPlace[] = [];
let countryCacheAt = 0;
let countryRefreshStarted = false;

/** Top-level region names only. Child locations stay out of the page. */
function slimCountries(res: any): SlimPlace[] {
	const rows = Array.isArray(res?.data) ? res.data : [];
	return rows
		.map((row: any) => ({
			id: row?.id,
			name: row?.name || '',
			slug: row?.slug || '',
		}))
		.filter((row: SlimPlace) => row.name);
}

function slimCategory(row: any): any {
	const children = Array.isArray(row?.child_categories)
		? row.child_categories.map(slimCategory).filter((child: { name: string }) => child.name)
		: [];
	const slim: any = {
		id: row?.id,
		name: row?.name || '',
		slug: row?.slug || '',
	};
	if (children.length) {
		slim.child_categories = children;
	}
	return slim;
}

function categoryRows(res: any): any[] {
	const rows = Array.isArray(res?.data) ? res.data : [];
	return rows.map(slimCategory).filter((row: { name: string }) => row.name);
}

/**
 * Fills a process cache for later renders. The current response does not wait on it,
 * and the request runs outside Angular so it cannot hold the page open.
 */
function refreshCountryNames(): void {
	if (!isPlatformServer(inject(PLATFORM_ID))) {
		return;
	}
	const fresh = cachedCountries.length > 0 && Date.now() - countryCacheAt < COUNTRY_CACHE_MS;
	if (fresh || countryRefreshStarted) {
		return;
	}
	countryRefreshStarted = true;
	inject(NgZone).runOutsideAngular(() => {
		fetch(`${DOMAIN}${GET_ADD_LOCATION_MENU_API}`, {
			headers: {
				Accept: 'application/json',
				requestFrom: SITE_REQUEST_FROM,
			},
		})
			.then((res) => (res.ok ? res.json() : null))
			.then((body) => {
				const rows = slimCountries(body);
				if (rows.length) {
					cachedCountries = rows;
					countryCacheAt = Date.now();
				}
			})
			.catch(() => {})
			.finally(() => {
				countryRefreshStarted = false;
			});
	});
}

function categoryRequest(mainService: MainService, categoryType: string): Observable<any[]> {
	return mainService.get_categories({
		category_for: 'experience',
		type: categoryType,
	}).pipe(
		map((res) => categoryRows(res)),
		catchError(() => of([])),
	);
}

export function listingFilterSources(
	mainService: MainService,
	categoryType: string,
): ListingFilterSources {
	refreshCountryNames();
	return {
		filterCountries: of(cachedCountries.map((row) => ({ ...row }))),
		filterCategories: categoryRequest(mainService, categoryType),
		filterHighLevel: categoryType === 'higher-level-category'
			? of([])
			: categoryRequest(mainService, 'higher-level-category'),
	};
}
